// 授权状态检查和自动重定向工具
export class AuthHelper {
  private static authCheckCache: { isAuthenticated: boolean; timestamp: number } | null = null;
  private static readonly CACHE_DURATION = 30000; // 30秒缓存

  /**
   * 检查用户授权状态
   */
  static async checkAuthStatus(): Promise<boolean> {
    // 检查缓存
    if (this.authCheckCache && Date.now() - this.authCheckCache.timestamp < this.CACHE_DURATION) {
      return this.authCheckCache.isAuthenticated;
    }

    try {
      const response = await fetch('/api/auth/status', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const isAuthenticated = data.authenticated || data.loggedIn || false;
        
        // 更新缓存
        this.authCheckCache = {
          isAuthenticated,
          timestamp: Date.now()
        };
        
        console.log('Auth status check result:', { isAuthenticated, data });
        return isAuthenticated;
      } else {
        console.log('Auth status check failed with status:', response.status);
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
    }
    
    return false;
  }

  /**
   * 清除授权状态缓存
   */
  static clearAuthCache() {
    this.authCheckCache = null;
  }

  /**
   * 执行需要授权的操作（下载或预览）
   * @param operation 操作函数
   * @param returnUrl 授权成功后返回的URL
   */
  static async executeWithAuth(
    operation: () => void | Promise<void>,
    returnUrl?: string
  ): Promise<void> {
    const isAuthenticated = await this.checkAuthStatus();
    
    if (isAuthenticated) {
      // 已授权，直接执行操作
      try {
        await operation();
      } catch (error) {
        console.error('Operation failed:', error);
        // 如果操作失败可能是token过期，清除缓存并重新授权
        this.clearAuthCache();
        this.redirectToAuth(returnUrl);
      }
    } else {
      // 未授权，跳转到授权页面
      this.redirectToAuth(returnUrl);
    }
  }

  /**
   * 重定向到授权页面
   * @param returnUrl 授权成功后返回的URL
   */
  private static redirectToAuth(returnUrl?: string) {
    const currentUrl = returnUrl || window.location.pathname + window.location.search;
    const authUrl = `/api/auth/login?returnUrl=${encodeURIComponent(currentUrl)}`;
    window.location.href = authUrl;
  }

  /**
   * 执行文件下载操作
   * @param projectId 项目ID
   * @param itemId 文件ID
   */
  static async downloadFile(projectId: string, itemId: string) {
    await this.executeWithAuth(async () => {
      try {
        const response = await fetch(`/api/download?projectId=${projectId}&itemId=${itemId}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.downloadUrl) {
          // 添加小延迟确保用户能看到加载状态
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // 下载文件并设置正确的文件名
          const downloadResponse = await fetch(data.downloadUrl);
          if (!downloadResponse.ok) {
            throw new Error(`Failed to download from S3: ${downloadResponse.status}`);
          }
          
          const blob = await downloadResponse.blob();
          const fileName = data.fileName || 'download';
          
          // 创建下载链接
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          throw new Error('No download URL received');
        }
      } catch (error) {
        console.error('🔍 AuthHelper: Download error:', error);
        throw error;
      }
    });
  }

  /**
   * 执行文件预览操作
   * @param itemId 文件ID
   * @param onPreview 预览回调函数
   */
  static async previewFile(
    itemId: string, 
    onPreview: (itemId: string) => void
  ) {
    await this.executeWithAuth(() => {
      onPreview(itemId);
    });
  }

  /**
   * 批量下载文件
   * @param projectId 项目ID
   * @param itemIds 文件ID列表
   */
  static async downloadMultipleFiles(projectId: string, itemIds: string[]) {
    await this.executeWithAuth(() => {
      const downloadUrl = `/api/download-multiple?projectId=${projectId}&itemIds=${itemIds.join(',')}`;
      window.open(downloadUrl, '_blank');
    });
  }
}